import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { agentsApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import type { Agent } from '@/domain/types';

/**
 * Creates or resets the credential an agent signs in with on their device.
 * Resetting also ends their existing sessions server-side, which is the point
 * when a handset has been lost.
 */
export function AgentLoginModal({
  isOpen,
  onClose,
  agent,
}: {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setUsername(agent?.username ?? '');
    setPassword('');
  }, [isOpen, agent]);

  const mutation = useMutation({
    mutationFn: () => agentsApi.setLogin(agent!.id, username.trim(), password),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      toast.success(t('agent.loginSet'));
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent?.hasLogin ? t('agent.resetLogin') : t('agent.createLogin')}
      description={agent?.name}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={username.trim().length < 3 || password.length < 8}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('auth.username')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="off"
          required
          autoFocus
        />
        <Input
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={t('auth.passwordRules')}
          autoComplete="new-password"
          required
        />
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {t('agent.loginSet')}
        </p>
      </div>
    </Modal>
  );
}
